import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

export type NotificationType = 'info' | 'success' | 'warning' | 'error';

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  imageUrl?: string;
  isAdmin?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  createdAt: Date;
  read: boolean;
  dismissed?: boolean;
  expiresAt?: Date;
}

interface NotificationState {
  notifications: Notification[];
  dismissedIds: Set<string>;
  addNotification: (notification: Omit<Notification, 'id' | 'createdAt' | 'read' | 'dismissed'>) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  clearAll: () => void;
  isDismissed: (id: string) => boolean;
  dismissNotification: (id: string) => void;
  cleanupExpiredNotifications: () => void;
}

export const useNotificationStore = create<NotificationState>()(
  persist(
    (set, get) => ({
      notifications: [],
      dismissedIds: new Set<string>(),
      
      addNotification: (notification) => {
        const id = uuidv4();
        if (!get().isDismissed(id)) {
          const now = new Date();
          const expiresAt = notification.expiresAt || new Date(now.getTime() + 24 * 60 * 60 * 1000); // 24h default
          
          set((state) => ({
            notifications: [
              {
                ...notification,
                id,
                createdAt: now,
                expiresAt,
                read: false,
                dismissed: false,
              },
              ...state.notifications,
            ].slice(0, 50), // Keep only last 50 notifications
          }));
        }
      },

      removeNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
        })),

      markAsRead: (id) =>
        set((state) => ({
          notifications: state.notifications.map((n) =>
            n.id === id ? { ...n, read: true } : n
          ),
        })),

      clearAll: () => {
        const { notifications, dismissedIds } = get();
        const newDismissedIds = new Set(dismissedIds);
        notifications.forEach(n => newDismissedIds.add(n.id));
        set({ 
          notifications: [],
          dismissedIds: newDismissedIds
        });
      },

      isDismissed: (id) => get().dismissedIds.has(id),

      dismissNotification: (id) =>
        set((state) => ({
          notifications: state.notifications.filter((n) => n.id !== id),
          dismissedIds: new Set([...state.dismissedIds, id])
        })),

      cleanupExpiredNotifications: () => {
        const now = new Date();
        set((state) => ({
          notifications: state.notifications.filter(
            (n) => !n.expiresAt || n.expiresAt > now
          ),
        }));
      },
    }),
    {
      name: 'notifications-storage',
      partialize: (state) => ({
        notifications: state.notifications.map(n => ({
          ...n,
          createdAt: n.createdAt.toISOString(),
          expiresAt: n.expiresAt?.toISOString(),
          action: undefined // Don't persist action handlers
        })),
        dismissedIds: Array.from(state.dismissedIds)
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Convert ISO strings back to Date objects
          state.notifications = state.notifications.map(n => ({
            ...n,
            createdAt: new Date(n.createdAt),
            expiresAt: n.expiresAt ? new Date(n.expiresAt) : undefined
          }));
          
          // Restore Set from array
          state.dismissedIds = new Set(state.dismissedIds);
          
          // Clean up expired notifications
          state.cleanupExpiredNotifications();
        }
      },
    }
  )
);